<template>
    <b-modal ref="modal" size="xl" static :title="title" hide-footer @hidden="reset">
        <form :action="storeUrl" method="POST" ref="form">
            <input type="hidden" name="_token" :value="csrf">
            <input type="hidden" name="article_id" :value="article ? article.id : ''">
            <input type="hidden" name="price_rule_id" :value="ruleId || ''">
            <input type="hidden" name="return_query" :value="returnQuery">

            <!--
                Aucun bloc de cette modale n'est retiré du DOM : vue-fragment, installé pour
                toute l'application, fige les nextSibling des voisins d'un nœud retiré, et
                Vue insère ensuite avant un nœud qui n'est plus là. D'où des v-show, et des
                conteneurs à enfant unique autour de ce qui doit être remonté.
            -->
            <div class="alert alert-danger" v-show="serverErrors.length">{{ serverErrors.join(' ') }}</div>

            <!-- L'article se choisit une fois : en modification, il est la règle elle-même.
                 Autocomplete standard de l'application, initialisé par app.js : son menu
                 s'ouvre dans body, hors du DOM que Vue gère. -->
            <div class="form-group" v-show="!locked">
                <label class="required" for="company_rule_article">Article</label>
                <input type="text" class="form-control model-autocomplete article-autocomplete" id="company_rule_article" ref="search"
                       placeholder="Rechercher un article par référence ou désignation…" autocomplete="off"
                       :data-api-url="autocompleteUrl" data-label="designation" data-value="designation"
                       data-force-value="true" data-target-input-id="company_rule_article_id">
                <input type="hidden" id="company_rule_article_id" ref="articleId">
            </div>

            <div v-show="loading" class="text-center ix-text-secondary py-4">
                <i class="far fa-spinner fa-spin"></i> Chargement…
            </div>

            <div v-show="context && !loading">
                <div class="alert alert-info" v-show="ruleId && !locked">
                    <i class="far fa-info-circle"></i>
                    Ce client a déjà une règle sur cet article : vous la modifiez.
                </div>

                <!-- Repères, pas réglages : ils se saisissent sur la fiche article. Une ligne
                     de lecture, l'article en titre, le reste en retrait. -->
                <div class="mb-3">
                    <div class="ix-text-bold">{{ articleLabel }}</div>
                    <div class="ix-text-secondary ix-text-small">{{ references }}</div>
                </div>

                <div class="form-group" v-show="!ruleId && sources.length">
                    <label for="company_rule_source">Reprendre une règle existante</label>
                    <div>
                        <select class="form-control" id="company_rule_source" :key="'source-' + formKey" v-model="sourceId" @change="applySource">
                            <option :value="null">Aucune : partir de zéro</option>
                            <option v-for="source in sources" :key="source.id" :value="source.id">
                                {{ source.label }} ({{ source.payload.tiers.length }} palier{{ source.payload.tiers.length > 1 ? 's' : '' }})
                            </option>
                        </select>
                    </div>
                    <small class="form-text text-muted">
                        La base et les paliers de la règle choisie sont copiés, puis modifiables librement.
                    </small>
                </div>

                <!-- Remonté à chaque article ou règle reprise : le composant lit ses
                     propriétés une seule fois, au montage. Seul enfant de son conteneur. -->
                <div>
                    <article-price-rule-form
                        v-if="context"
                        :key="'form-' + formKey"
                        :old-rule="JSON.stringify(formRule)"
                        :grid-profiles="JSON.stringify(context.grid_profiles)"
                        :grid-enabled="context.grid_enabled"
                        :is-scale="false"
                        :public-price="context.article.selling_price"></article-price-rule-form>
                </div>

                <div class="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        <button type="button" class="ix-btn link text-danger" v-show="ruleId" @click="destroy">
                            <i class="far fa-trash"></i> Supprimer la règle
                        </button>
                    </div>
                    <button type="submit" class="ix-btn green">Enregistrer</button>
                </div>
            </div>
        </form>

        <form :action="destroyAction" method="POST" ref="destroyForm" class="d-none">
            <input type="hidden" name="_token" :value="csrf">
            <input type="hidden" name="_method" value="DELETE">
            <input type="hidden" name="return_query" :value="returnQuery">
        </form>
    </b-modal>
</template>

<script>
    import $ from 'jquery';
    import Swal from 'sweetalert2';
    import ArticlePriceRuleForm from '../stock/ArticlePriceRuleForm';

    /**
     * Ajout et modification d'une règle tarifaire depuis la fiche société : l'article,
     * puis la même saisie que sur la fiche article.
     *
     * Les boutons de la page l'ouvrent par l'évènement « company-price-rule:open », avec
     * l'article à modifier ou sans rien pour un ajout.
     *
     * @see docs/DRI/Phase1/fiche-societe-politique-tarifaire.md
     */
    export default {
        name: 'company-price-rule-modal',
        components: { ArticlePriceRuleForm },
        props: ['contextUrl', 'storeUrl', 'destroyUrl', 'autocompleteUrl', 'returnQuery', 'csrf', 'oldInput', 'errors'],
        data() {
            return {
                article: null,
                context: null,
                loading: false,
                locked: false,
                ruleId: null,
                sourceId: null,
                formRule: null,
                formKey: 0,
                serverErrors: [],
            };
        },
        computed: {
            title() {
                return this.ruleId ? 'Modifier la règle tarifaire' : 'Ajouter une règle tarifaire';
            },
            articleLabel() {
                return this.context ? this.context.article.label : '';
            },
            sources() {
                return this.context ? this.context.sources : [];
            },
            /** Les repères de l'article pour ce client, sans les absents, en une ligne. */
            references() {
                if (!this.context) return '';

                const article = this.context.article;
                const items = ['Prix tarif ' + this.money(article.selling_price) + ' HT'];

                if (this.context.grid_enabled) {
                    items.push('Remise du profil ' + (this.context.grid_label || 'aucune'));
                }
                if (article.minimum_order_quantity) {
                    items.push('Quantité minimale ' + this.number(article.minimum_order_quantity) + ' ' + article.unit);
                }
                if (article.minimum_selling_price !== null) {
                    items.push('Prix plancher ' + this.money(article.minimum_selling_price) + ' HT');
                }

                return items.join(' · ');
            },
            destroyAction() {
                return this.ruleId ? this.destroyUrl.replace('RULE_ID', this.ruleId) : '';
            },
        },
        methods: {
            open(detail) {
                this.reset();

                if (detail && detail.articleId) {
                    this.locked = true;
                    this.select({ id: detail.articleId, label: detail.articleLabel });
                }

                this.$refs.modal.show();
            },
            reset() {
                this.article = null;
                this.context = null;
                this.locked = false;
                this.ruleId = null;
                this.sourceId = null;
                this.formRule = null;
                this.serverErrors = [];
                $(this.$refs.search).val('');
                $(this.$refs.articleId).val('');
            },
            /** Le contexte de l'article, et sa règle existante qui fait basculer en modification. */
            select(article, oldRule = null) {
                this.article = article;
                this.loading = true;

                return axios.get(this.contextUrl, { params: { article_id: article.id } }).then(response => {
                    this.context = response.data;
                    this.ruleId = response.data.rule ? response.data.rule.id : null;
                    this.sourceId = null;
                    this.formRule = oldRule || response.data.rule;
                    this.formKey++;
                }).finally(() => {
                    this.loading = false;
                });
            },
            /** Rien n'est écrit avant « Enregistrer » : la règle reprise ne fait que remplir le formulaire. */
            applySource() {
                const source = this.context.sources.find(item => item.id === this.sourceId);
                this.formRule = source ? Object.assign({}, source.payload, { id: null }) : null;
                this.formKey++;
            },
            destroy() {
                Swal.fire({
                    title: 'Supprimer cette règle ?',
                    text: 'Le client repassera au meilleur prix entre la règle par défaut et la remise de son profil. La suppression reste lisible dans l\'historique.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Supprimer',
                    cancelButtonText: 'Annuler',
                }).then(result => {
                    if (!result.value) return;
                    // requestSubmit déclenche l'évènement submit, que la page écoute pour
                    // revenir à la même position après rechargement.
                    const form = this.$refs.destroyForm;
                    form.requestSubmit ? form.requestSubmit() : form.submit();
                });
            },
            money(value) {
                return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
            },
            number(value) {
                return Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
            },
        },
        mounted() {
            // L'autocomplete standard écrit l'article choisi dans ce champ caché.
            $(this.$refs.articleId).on('change', () => {
                const id = $(this.$refs.articleId).val();
                if (id) this.select({ id: parseInt(id, 10) });
            });

            window.addEventListener('company-price-rule:open', event => this.open(event.detail));

            // Une validation refusée rouvre la modale sur la saisie en cours.
            const old = JSON.parse(this.oldInput || 'null');
            if (old && old.article_id) {
                this.$refs.modal.show();
                this.locked = !!old.price_rule_id;
                this.select({ id: old.article_id }, {
                    id: old.price_rule_id || null,
                    tier_base: old.tier_base,
                    ends_on: old.ends_on,
                    comment: old.comment,
                    tiers: old.tiers || [],
                }).then(() => {
                    this.serverErrors = JSON.parse(this.errors || '[]');
                    $(this.$refs.search).val(this.context ? this.context.article.label : '');
                });
            }
        },
    };
</script>
